import { getRouteApi, Link, useRouterState } from "@tanstack/react-router";
import { cn } from "@workspace/web-ui/lib/utils";
import { useState } from "react";

import { NavigationItem, urgentCareNavigationItems, weightLossNavigationItems, medicalsNavigationItems, platformSwitchItems } from "~/features/dashboard/data/navigation";
// import { useLogout } from "~/features/auth/api/mutations";
import { useLogoutModal } from "~/features/dashboard/pages/settings/store/logout-store";
import { LogoutConfirmModal } from "~/features/dashboard/pages/settings/components/logout-modal";

import { ChevronDown, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useTaskStore } from "../store/task";
import { AlfieChatBot } from "./alfie-chat";
import { useAuth } from "~/providers/auth.provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/web-ui/components/dropdown-menu";
import { Button } from "@workspace/web-ui/components/button";

export function DashboardSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [isOpen, setIsOpen] = useState(false);
  const [showChatBot, setShowChatBot] = useState(false);
  const [isTopDropdownOpen, setIsTopDropdownOpen] = useState(false);
  const { taskCount } = useTaskStore()
  const { user, logout: authLogout } = useAuth()
  const { open: openLogoutModal } = useLogoutModal()
  // const logoutMutation = useLogout()

  // Determine current platform
  const isWeightLossPlatform = pathname.startsWith("/dashboard/weight-loss");
  const isMedicalsPlatform = pathname.startsWith("/dashboard/medicals");
  const currentItems = isWeightLossPlatform ? weightLossNavigationItems : isMedicalsPlatform ? medicalsNavigationItems : urgentCareNavigationItems;
  const currentTitle = isWeightLossPlatform ? "Weight Loss" : isMedicalsPlatform ? "Medicals" : "Urgent Care";
  const currentInitials = isWeightLossPlatform ? "WL" : isMedicalsPlatform ? "MD" : "UC";

  // check if the platform is active
  const isActivePlatform = (platform: string) => {
    return pathname.startsWith(platform);
  }

  function getActiveItem(items: NavigationItem[], pathname: string): string | null {
    let bestMatch: { url: string, length: number } | null = null;
    for (const item of items) {
      if (item.url === pathname) {
        return item.url;
      }
      if (item.matchUrls?.length) {
        for (const matchUrl of item.matchUrls) {
          if (pathname.startsWith(matchUrl)) {
            if (!bestMatch || matchUrl.length > bestMatch.length) {
              bestMatch = { url: item.url, length: matchUrl.length };
            }
          }
        }
      }
    }
    return bestMatch ? bestMatch.url : null;
  }

  const activeItemUrl = getActiveItem(currentItems, pathname);

  const handlePlatformSwitch = (targetPlatform: 'weight-loss' | 'urgent-care' | 'medicals' | string) => {
    // Only show chat bot when switching FROM weight loss TO urgent care
    if ((isWeightLossPlatform || isMedicalsPlatform) && targetPlatform === 'urgent-care') {
      setShowChatBot(true);
      // Don't navigate yet - let the chatbot handle the navigation
      setIsOpen(false);
      setIsTopDropdownOpen(false);
      // setMenuOpen(false);
      return;
    }
    setIsOpen(false);
    setIsTopDropdownOpen(false);
    // setMenuOpen(false);
  };

  const handleLogout = () => {
    openLogoutModal()
    setIsTopDropdownOpen(false);
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className=" h-full w-[286px] flex-col bg-[#0A1A2F] sm:flex">
        <div className="flex h-full flex-col p-4">
          {/* User Profile Dropdown */}
          <DropdownMenu onOpenChange={setIsTopDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "mb-8 flex w-full items-center rounded-[12px] justify-start gap-2 py-6 px-2 cursor-pointer hover:bg-[#1E293B]",
                  isTopDropdownOpen && "border-2 border-[#4272DD]"
                )}
              >
                <div className="flex h-8 w-8 items-center justify-center border-[1.7px] border-[#4272DD] rounded-md bg-[#FFFFFF1A] shadow-inner shadow-[#FFFFFF26] text-white">
                  <span className="text-xs font-bold">{currentInitials}</span>
                </div>
                <div className="flex flex-col text-start">
                  <span className="text-sm font-medium text-white">{currentTitle}</span>
                  {user && (<span className="text-xs text-[#667281]">{user?.first_name} {user?.last_name}</span>)}
                </div>
                <ChevronsUpDown className="ml-auto h-6 w-6 text-[#667281]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-[12px] bg-[#1C2E47] border border-[#667281]/50 shadow-lg p-0 gap-0">
              <div className="flex flex-col divide-y divide-[#FFFFFF1A]">
              {platformSwitchItems?.map((item) => {
                const isActive = isActivePlatform(item.url);
                return (
                  <Link
                    key={item.url}
                    to={item.url}
                    onClick={
                      () => {
                        if (item.disabled) return;
                        handlePlatformSwitch(item.platform)
                      }
                    }
                    className={`flex w-full items-center py-[14px] px-3 justify-between bg-transparent text-white hover:bg-[#FFFFFF0D]`}
                    disabled={item.disabled}
                  >
                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{!isActive && "Switch to "}{item.title} </span>
                      {!isActive && <ChevronRight size={16} />}
                      </div>
                      {isActive && <p className="text-xs text-white opacity-[50%]">Currently active here</p>}
                    </div>
                  </Link>
                )
              })}
              </div>
              <DropdownMenuSeparator className="bg-[#667281]/50 h-[1px]" />
              {/* <DropdownMenuItem onClick={handleLogout} className=""> */}
              <button onClick={handleLogout} className="flex w-full py-[14px] px-3 items-center justify-between bg-transparent text-[#667281] hover:bg-[#FFFFFF0D]">
                        <div className="flex items-center gap-2">
                          <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.00003 2.0013C4.78003 2.0013 2.84075 3.20664 1.80232 5.00179C1.61796 5.32049 1.21014 5.4294 0.891432 5.24504C0.572725 5.06068 0.463816 4.65286 0.648179 4.33415C1.91514 2.14395 4.28483 0.667969 7.00003 0.667969C11.0501 0.667969 14.3334 3.95121 14.3334 8.0013C14.3334 12.0514 11.0501 15.3346 7.00003 15.3346C4.28483 15.3346 1.91514 13.8586 0.648179 11.6685C0.463816 11.3497 0.572725 10.9419 0.891432 10.7576C1.21014 10.5732 1.61796 10.6821 1.80232 11.0008C2.84075 12.796 4.78003 14.0013 7.00003 14.0013C10.3137 14.0013 13 11.315 13 8.0013C13 4.68759 10.3137 2.0013 7.00003 2.0013Z" fill="#C85858"/>
<path d="M6.52864 4.86323C6.78899 4.60288 7.2111 4.60288 7.47145 4.86323L10.1381 7.5299C10.3985 7.79025 10.3985 8.21236 10.1381 8.47271L7.47145 11.1394C7.2111 11.3997 6.78899 11.3997 6.52864 11.1394C6.26829 10.879 6.26829 10.4569 6.52864 10.1966L8.05723 8.66797H1.00004C0.631851 8.66797 0.333374 8.36949 0.333374 8.0013C0.333374 7.63311 0.631851 7.33463 1.00004 7.33463H8.05723L6.52864 5.80604C6.26829 5.54569 6.26829 5.12358 6.52864 4.86323Z" fill="#C85858"/>
</svg>

                          <span className="text-sm font-medium text-white">Log out</span>
                        </div>
                      </button>
              {/* </DropdownMenuItem> */}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Navigation */}
          <nav className="flex flex-col">
            {currentItems.map((item) => {
              const isActive = item.url === activeItemUrl;

              if (isMedicalsPlatform) {
                const routeApi = getRouteApi("/(dashboard)/dashboard/medicals/$program");
                const { program } = routeApi.useParams();

                return (
                  <Link
                    key={item.url}
                    to={item?.isDynamicRoute ? item?.dynamicUrl?.(program) : item.url}
                    className={cn(
                      "flex items-center rounded-md px-3 py-2.5",
                      isActive
                        ? "bg-[#FFFFFF0D] border border-[#FFFFFF0D] text-white"
                        : "text-[#667281] hover:bg-[#1E293B]/50"
                    )}
                  >
                    {item.icon && (
                      <span className="mr-3 text-lg">{item.icon}</span>
                    )}
                    <span className="text-sm">{item.title}</span>
                    {taskCount > 0 && item.title === "Home" && (
                      <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#4272DD] shadow-inner shadow-[#FFFFFF40] text-xs font-medium text-white">
                        {taskCount > 0 ? taskCount : ""}
                      </span>
                    )}
                  </Link>
                );
              }

              return (
                <Link
                  key={item.url}
                  to={item.url}
                  className={cn(
                    "flex items-center rounded-md px-3 py-2.5",
                    isActive
                      ? "bg-[#FFFFFF0D] border border-[#FFFFFF0D] text-white"
                      : "text-[#667281] hover:bg-[#1E293B]/50"
                  )}
                >
                  {item.icon && (
                    <span className="mr-3 text-lg">{item.icon}</span>
                  )}
                  <span className="text-sm">{item.title}</span>
                  {taskCount > 0 && item.title === "Home" && (
                    <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#4272DD] shadow-inner shadow-[#FFFFFF40] text-xs font-medium text-white">
                        {taskCount > 0 ? taskCount : ""}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Platform Switcher */}
          <div className="mt-auto lg:hidden flex">
            <div className="rounded-md p-2">
              <div className="text-sm text-[#667281] px-2 pb-2">
                Switch Platforms
              </div>
              <div className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex w-full items-center border border-[#4272DD] justify-between rounded-md bg-[#1E293B] px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white">{currentTitle}</span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-[#667281]" />
                </button>

                {isOpen && (
                  <div className="absolute bottom-full w-full rounded-[12px] bg-[#1C2E47] mb-2 shadow-lg">
                    <div className="flex flex-col">
                      <div className="flex flex-col divide-y divide-[#FFFFFF1A]">
                      {platformSwitchItems?.map((item,index) => {
                        const isActive = isActivePlatform(item.url);
                        return (
                          <Link
                          key={index}
                            to={item.url}
                            onClick={
                              () => {
                                if (item.disabled) return;
                                handlePlatformSwitch(item.platform)
                              }
                            }
                            className={`flex w-full items-center py-[14px] px-3 justify-between bg-transparent text-white hover:bg-[#FFFFFF0D]`}
                            disabled={item.disabled}
                          >
                            <div className="flex flex-col gap-2 w-full">
                              <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{!isActive && "Switch to "}{item.title} </span>
                              {!isActive && <ChevronRight size={16} />}
                              </div>
                              {isActive && <p className="text-xs text-white opacity-[50%]">Currently active here</p>}
                            </div>
                          </Link>
                        )
                      })}

                      </div>
                      <div className="bg-[#667281]/50 h-[1px]" />
                      <button onClick={handleLogout} className="flex w-full py-[14px] px-3 items-center justify-between rounded-md bg-transparent text-[#667281] hover:bg-[#FFFFFF0D]">
                        <div className="flex items-center gap-2">
                          <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.00003 2.0013C4.78003 2.0013 2.84075 3.20664 1.80232 5.00179C1.61796 5.32049 1.21014 5.4294 0.891432 5.24504C0.572725 5.06068 0.463816 4.65286 0.648179 4.33415C1.91514 2.14395 4.28483 0.667969 7.00003 0.667969C11.0501 0.667969 14.3334 3.95121 14.3334 8.0013C14.3334 12.0514 11.0501 15.3346 7.00003 15.3346C4.28483 15.3346 1.91514 13.8586 0.648179 11.6685C0.463816 11.3497 0.572725 10.9419 0.891432 10.7576C1.21014 10.5732 1.61796 10.6821 1.80232 11.0008C2.84075 12.796 4.78003 14.0013 7.00003 14.0013C10.3137 14.0013 13 11.315 13 8.0013C13 4.68759 10.3137 2.0013 7.00003 2.0013Z" fill="#C85858"/>
<path d="M6.52864 4.86323C6.78899 4.60288 7.2111 4.60288 7.47145 4.86323L10.1381 7.5299C10.3985 7.79025 10.3985 8.21236 10.1381 8.47271L7.47145 11.1394C7.2111 11.3997 6.78899 11.3997 6.52864 11.1394C6.26829 10.879 6.26829 10.4569 6.52864 10.1966L8.05723 8.66797H1.00004C0.631851 8.66797 0.333374 8.36949 0.333374 8.0013C0.333374 7.63311 0.631851 7.33463 1.00004 7.33463H8.05723L6.52864 5.80604C6.26829 5.54569 6.26829 5.12358 6.52864 4.86323Z" fill="#C85858"/>
</svg>

                          <span className="text-sm font-medium text-white">Log out</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <AlfieChatBot open={showChatBot} onOpenChange={setShowChatBot} />
      </aside>
      <LogoutConfirmModal logout={authLogout} />
    </>
  );
}
